// Angular + external
import { ChangeDetectorRef, Component, ElementRef, ViewChild } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { BfxDialogBaseComponent } from "./bfx-dialog-base.component";
import { BfxDialogData } from "./models/bfx-dialog-data.model";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { BfxAutoCompleteComponent } from "app/bfx-element-library/bfx-autocomplete.component";
import { NativeDesktopSupport } from "app/bfx-native-desktop/native-desktop-support";
import {
  ActiveUserProfilePromiseService,
  Roles,
  SystemConfigPromiseService,
} from "shared/auth-services";
import { PlatformProfileImpl } from "shared/cubiq-model-multi-asset";
import { PlatformProfilePrimaryLookupService } from "shared/cubiq-services/lookup/model/impl/PlatformProfilePrimaryLookupService";

const SYSTEM_DEFAULT_PLATFORM_PROFILE = { value: null, label: "System Default" };

/** Value type use in autocomplete */
type AutocompleteType = { value: PlatformProfileImpl; label: string };

/**
 * Dialog component for switching a user profile - all actions and error-handling are handled by user-supplied callbacks
 */
@Component({
  selector: "bfx-dialog-switch-profile",
  templateUrl: "./html/bfx-dialog-switch-profile.html",
  standalone: false,
})
export class BfxDialogSwitchProfileComponent extends BfxDialogBaseComponent {
  @ViewChild("platformProfileSelection") platformProfileSelection: BfxAutoCompleteComponent;

  /** Currently selected value - defaults to System Default */
  public platformProfile: AutocompleteType = SYSTEM_DEFAULT_PLATFORM_PROFILE;

  /** List of dropdown options */
  public platformProfileSuggestions: AutocompleteType[] = [SYSTEM_DEFAULT_PLATFORM_PROFILE];

  /** Channel for sending messages back to dialog service */
  private dialogEventSubject = new Subject<BfxDialogData>();

  public data = {
    dialogId: "",
    payload: null,
    body: null,
    dialogData: {
      show: false,
      width: 450,
      height: 150,
      switchEnabled: true,
      title: "Title",
      dropdownLabel: "Label",
      warningMessage1: "",
      warningMessage2: "Blah restart",
    },
  };

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(
    private platformProfilesService: PlatformProfilePrimaryLookupService,
    private activeUserService: ActiveUserProfilePromiseService,
    private systemConfigService: SystemConfigPromiseService,
    private translate: TranslateService,
    private element: ElementRef,
    private cd: ChangeDetectorRef,
  ) {
    super();
    this.init();
  }

  private init() {
    this.data.dialogData.title = this.translate.instant("switch.profile.title");
    this.data.dialogData.dropdownLabel = this.translate.instant("switch.profile.dropdown.label");
    const allProfilesPr = this.platformProfilesService.getPlatformProfiles();
    const activeUserPr = this.activeUserService.getActiveUserData();
    Promise.all([allProfilesPr, activeUserPr]).then(([profiles, activeUser]) => {
      const profileId = activeUser.defaultPublicProfileId;
      if (profileId) {
        const profileIdStr = profileId.toString();
        // Find the current profile for user and use this as the initial value
        const existing = profiles.find((profile) => profile.profileId == profileIdStr);
        if (existing) {
          this.platformProfile = { value: existing, label: existing.profileName };
        }
      }
    });

    this.systemConfigService.hasRole(Roles.ROLE_ADMIN_PUBLIC_PROFILES).then((value: boolean) => {
      logger.out.info(`switch-profile: can save =  ${value}`);
      const message1Id = value
        ? "switch.profile.warning.message1.save"
        : "switch.profile.warning.message1.nosave";
      this.data.dialogData.warningMessage1 = this.translate.instant(message1Id);
    });

    this.data.dialogData.warningMessage2 = this.translate.instant(
      "switch.profile.warning.message2",
    );
  }

  public openDialog(dialogData: BfxDialogData): Observable<any> {
    logger.out.info("openDialog");

    this.data.dialogId = dialogData.dialogId;
    this.data.body = dialogData.body;
    this.data.payload = dialogData.payload;
    NativeDesktopSupport.prepareEmbeddedDialog(this.data.body, this._dialog, this.element);

    this.data.dialogData.show = true;

    return this.dialogEventSubject.asObservable();
  }

  public closeDialog(): void {
    logger.out.info("closeDialog");
    super.closeDialog();

    this.data.payload = null;
    this.data.dialogData.show = false;
    // inform dialog service that dialog is closed and to cleanup
    this.notifyDialogClosed(this.dialogEventSubject, this.data.dialogId);
  }

  /** Halnder for Switch Profile action*/
  public switchProfileYes(): void {
    logger.out.info("switchProfile");
    if (this.data.payload?.callback) {
      this.data.payload.callback(this.platformProfile?.value);
    }
    this.closeDialog();
  }

  /**
   * Handler for cancel action
   */
  public switchProfileCancel(): void {
    logger.out.info("actionNo");
    if (this.data.payload?.callbackNegative) {
      this.data.payload.callbackNegative();
    }
    this.closeDialog();
  }

  public onSelectPlatformProfile(data: any): void {
    this.platformProfile = data;
    this.data.dialogData.switchEnabled = this.platformProfile !== null;
  }

  public searchPlatformProfile(event: any): void {
    this.platformProfilesService
      .getPlatformProfiles()
      .then((results: Array<PlatformProfileImpl>) => {
        const adaptedResults = results
          .map((profile) => {
            return { value: profile, label: profile.profileName };
          })
          .sort((a, b) => a.label?.localeCompare(b.label) ?? (b.label ? -1 : 0));
        // Add in the System Default at the top after sorting
        adaptedResults.unshift(SYSTEM_DEFAULT_PLATFORM_PROFILE);
        if (
          event != null &&
          typeof event.query == "string" &&
          event.query != "" &&
          Array.isArray(results)
        ) {
          const lcQuery = event.query.toLowerCase();

          this.platformProfileSuggestions = adaptedResults.filter((result) =>
            result.label.toLowerCase().includes(lcQuery),
          );
        } else {
          this.platformProfileSuggestions = adaptedResults;
        }
        this.cd.detectChanges();
      });
  }
}
